import { IsVisible } from '@zg-rentals/react-use';
import { Box, List, Paragraph, TextButton } from '@zillow/constellation-10';
import {
  useConversationList_Rh_GetLatestConversationsQuery,
  useMessageList_Rh_GetConversationLazyQuery,
} from '../../types/GraphqlTypes';
import type { ConversationList_Rh_GetLatestConversationsQuery } from '../../types/GraphqlTypes';
import ConfigConstants from '../../constants/ConfigConstants';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import arrayUtils from '../../utils/arrayUtils';
import useSmartPolling from '../../hooks/useSmartPolling';
import type { UpdateConversationStateArgs } from '../../types/UpdateConversationStateArgs';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { AppContext } from '../../contexts/AppContextProvider';
import brandUtils from '../../utils/brandUtils';
import NoConversationsContent from '../NoConversationsContent';
import { logError } from '@zg-rentals/log-error';
import { bootstrap } from '../../bootstrap';
import { useWebSocketContext } from '../../contexts/WebSocketContextProvider';
import { WEBSOCKET_DEBUG } from '../../hooks/useWebsocketFeature';
import type { WebSocketMessage } from '../../hooks/useWebSocketConnection';
import { emitConversationItemClickEvent } from '../../constants/clickstreamEvents/conversationItemClick';
import { ConversationItemSkeleton, ConversationListSkeleton } from './skeleton';
import ConversationItemContainer from './components/ConversationItemContainer';

const POLL_INTERVAL_IN_SECONDS = 60;
const { MAX_DAYS_BACK_FOR_CONVERSATIONS } = ConfigConstants;
const POLL_INTERVAL_IN_MS = POLL_INTERVAL_IN_SECONDS * 1000;

const logger = bootstrap.logger.child({ name: 'ConversationList' });

export const getConversationToRedirectToNext = (
  currentConversationId: string,
  conversations: ConversationList_Rh_GetLatestConversationsQuery['RH_getLatestConversations']['conversations'],
) => {
  let nextConversationId = '';

  if (conversations.length > 1) {
    const currentConversationIndex = conversations.findIndex(
      (conversation) => conversation.conversationId === currentConversationId,
    );

    if (currentConversationIndex !== -1) {
      const nextConversationIndex = (currentConversationIndex + 1) % conversations.length;
      nextConversationId = conversations[nextConversationIndex].conversationId || '';
    } else {
      nextConversationId = conversations[0].conversationId || '';
    }
  }

  return nextConversationId;
};

export type ConversationInConversationList = ArrayElement<
  ConversationList_Rh_GetLatestConversationsQuery['RH_getLatestConversations']['conversations']
>;

type ConversationListProps = {
  shouldFetchOlderConversations: boolean;
  showMessageListOnMobile(): void;
  updateConversationState(args: UpdateConversationStateArgs): void;
};

export default function ConversationList({
  shouldFetchOlderConversations,
  showMessageListOnMobile,
  updateConversationState,
}: ConversationListProps) {
  const { conversationId, folder, mobileView } = useContext(InboxPageContext);
  const { brand } = useContext(AppContext);
  const isHotPadsBrand = brandUtils.isHotpads(brand);
  const [daysBack, setDaysBack] = useState<number | null>(
    shouldFetchOlderConversations ? MAX_DAYS_BACK_FOR_CONVERSATIONS : null,
  );
  const [isLoadingOlderConversations, setIsLoadingOlderConversations] = useState(false);

  const [fetchLatestMessages] = useMessageList_Rh_GetConversationLazyQuery({
    variables: {
      conversationId: conversationId || '',
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    ssr: false,
  });

  // WebSocket integration
  const { subscribeToMessages, hadErrorSinceLastOpen, isWebSocketEnabledAndReady } = useWebSocketContext();

  const {
    data: latestConversationsData,
    error,
    fetchMore,
    refetch,
    startPolling,
    stopPolling,
    variables,
    networkStatus,
  } = useConversationList_Rh_GetLatestConversationsQuery({
    variables: {
      daysBack,
      folder,
    },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    ssr: false,
    fetchPolicy: 'network-only',
    // Only use Apollo polling when WebSocket is not enabled and ready
    pollInterval: isWebSocketEnabledAndReady ? undefined : POLL_INTERVAL_IN_MS,
    onCompleted() {
      if (isLoadingOlderConversations) {
        setIsLoadingOlderConversations(false);
      }

      // Network status 6 indicates a poll and 4 indicates a refetch
      if (networkStatus === 6 || (networkStatus === 4 && !isWebSocketEnabledAndReady)) {
        fetchLatestMessages();

        if (networkStatus === 6) {
          updateLastPollTimestamp();
        }
      }
    },
    onError(err) {
      logError({
        error: err,
        context: {
          info: 'Error fetching conversations in ConversationList',
          networkStatus,
          isNetworkError: !!err.networkError,
        },
      });
    },
  });

  const { updateLastPollTimestamp } = useSmartPolling({
    pollIntervalMs: POLL_INTERVAL_IN_MS,
    refetch,
    startPolling,
    stopPolling,
    isEnabled: !isWebSocketEnabledAndReady, // Enable when WebSocket is not enabled and ready
  });

  const handleWebSocketMessage = useCallback(
    async (message: WebSocketMessage) => {
      if (message.event?.type === 'conversationUpdated' || message.event?.type === 'newConversation') {
        if (WEBSOCKET_DEBUG) {
          logger.info('ConversationList: Refetching due to event type:', { eventType: message.event.type });
        }
        await refetch();
      }
    },
    [refetch],
  );

  useEffect(() => {
    if (!isWebSocketEnabledAndReady) return;
    return subscribeToMessages(handleWebSocketMessage);
  }, [isWebSocketEnabledAndReady, subscribeToMessages, handleWebSocketMessage]);

  useEffect(() => {
    if (!isWebSocketEnabledAndReady || !hadErrorSinceLastOpen) return;
    refetch();
  }, [isWebSocketEnabledAndReady, hadErrorSinceLastOpen, refetch]);

  const { RH_getLatestConversations } = latestConversationsData || {};
  const conversations = useMemo(
    () => arrayUtils.removeDuplicates(RH_getLatestConversations?.conversations || [], 'conversationId'),
    [RH_getLatestConversations],
  );
  const hasNextPage = RH_getLatestConversations?.pagination?.hasNextPage || false;
  const showLoadMore = !hasNextPage && !shouldFetchOlderConversations;
  const shouldShowNoConversationsContent = conversations.length === 0 && !error;
  const shouldShowLoadingSkeleton = !latestConversationsData && !error && !isLoadingOlderConversations;

  const handleUpdateConversations = () => {
    return updateConversationState({
      conversationId: conversationId || '',
      hasLoadedOlderConversations: true,
      showConversationList: mobileView === InboxPageMobileView.ConversationList,
    });
  };

  const handleFetchMoreConversations = async (overrideShouldFetchOlderConversations = false) => {
    const updatedDaysBack =
      overrideShouldFetchOlderConversations || shouldFetchOlderConversations ? MAX_DAYS_BACK_FOR_CONVERSATIONS : null;
    try {
      await fetchMore({
        variables: {
          paginationStartIndex: conversations.length ? conversations[conversations.length - 1].conversationId : null,
          folder,
          daysBack: updatedDaysBack,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          const newConversations = fetchMoreResult.RH_getLatestConversations.conversations;
          const updatedConversations = [...previousResult.RH_getLatestConversations.conversations, ...newConversations];
          const updatedUniqueConversations = arrayUtils.removeDuplicates(updatedConversations || [], 'conversationId');

          return {
            ...previousResult,
            RH_getLatestConversations: {
              ...previousResult.RH_getLatestConversations,
              conversations: updatedUniqueConversations,
              pagination: fetchMoreResult.RH_getLatestConversations.pagination,
            },
          };
        },
      });

      if (variables) {
        variables.daysBack = updatedDaysBack;
      }
      setDaysBack(updatedDaysBack);
    } catch (err) {
      logError({ error: err, context: 'Error fetching more conversations' });
    }
  };

  const handleLoadOlderConversationsClick = async () => {
    try {
      logger.info('User clicked to load older conversations, getting latest conversations');
      setIsLoadingOlderConversations(true);
      handleFetchMoreConversations(true);

      return updateConversationState({
        hasLoadedOlderConversations: true, // This persists the daysBack variable as 660 when changing folders
        conversationId: conversationId || '',
        shouldChangeRoute: false,
      });
    } catch (err) {
      logError({ error: err, context: 'Error fetching latest conversations on load older conversations click' });
    }
  };

  const handleConversationItemVisibilityChange = (isVisible: boolean) => {
    if (isVisible) {
      handleFetchMoreConversations();
    }
  };

  const handleConversationItemClick = useCallback(
    (selectedConversationId: string) => {
      if (selectedConversationId === conversationId) {
        showMessageListOnMobile();
        return;
      }
      emitConversationItemClickEvent();

      if (selectedConversationId) {
        updateConversationState({ conversationId: selectedConversationId, showConversationList: false });
      }
    },
    [conversationId, showMessageListOnMobile, updateConversationState],
  );

  if (shouldShowLoadingSkeleton) return <ConversationListSkeleton />;

  if (error && !latestConversationsData) {
    return (
      <Box
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Paragraph>There was an error fetching conversations.</Paragraph>
      </Box>
    );
  }

  if (shouldShowNoConversationsContent) {
    return <NoConversationsContent showErrorExperience={false} updateConversationState={handleUpdateConversations} />;
  }

  return (
    <List.Root
      aria-label="conversations"
      data-testid="conversation-list"
      appearance="none"
      css={{
        overflowY: 'auto',
      }}
    >
      {conversations.map((conversation) => (
        <ConversationItemContainer
          key={conversation.conversationId}
          conversation={conversation}
          onClick={handleConversationItemClick}
        />
      ))}

      {hasNextPage && (
        <IsVisible onChange={handleConversationItemVisibilityChange} options={{ rootMargin: '-100px 0px 0px 0px' }}>
          <ConversationItemSkeleton />
          <ConversationItemSkeleton />
        </IsVisible>
      )}

      {showLoadMore && (
        <TextButton onClick={handleLoadOlderConversationsClick} css={{ paddingBlock: 'default', width: '100%' }}>
          Load older conversations
        </TextButton>
      )}
      {isLoadingOlderConversations && (
        <Paragraph
          textStyle="body-bold"
          css={{
            textAlign: 'center',
            paddingTop: 'default !important',
            paddingBottom: 'default !important',
            borderRight: '1px solid {colors.gray.300}',
            color: isHotPadsBrand ? '#007a70 !important' : '{colors.blue.500}',
          }}
        >
          Loading older conversations...
        </Paragraph>
      )}
    </List.Root>
  );
}
